import classNames from 'classnames';
import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import { branch, compose, lifecycle, renderComponent, renderNothing, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { links } from '../../../../../constants';
import { 
  handleInvalidForm,
  isBusinessAdmin,
  notCanceled,
  isBusinessAdminOnly,
  isBusinessUser,
  getIndividualPlanIdByUserIndustry,
  getSmallTeamPlanIdByUserIndustry,
} from '../../../../../services';
import { businessActions, businessSelector } from '../../../../../state/modules/business';
import { stripeConfigSelector } from '../../../../../state/modules/configuration/selectors';
import { paymentActions } from '../../../../../state/modules/payment';
import { currentUserSelector, userActions } from '../../../../../state/modules/user';
import { CreditCardElements, FormSubmissionMessage } from '../../../../components';
import { withStripe } from '../../../../components/higher-order';
import Loading from '../../../loading';
import { PlanDetails, PlanUpgrade } from '../components';
import TeamMemberPayment from './team-member-payment';

const stripeErrorPrefix = "Stripe Error - ";

const CardError = branch(
  ({ cardError }) => !cardError,
  renderNothing
)(({ cardError }) => (
  <div aria-invalid='true' className='has-error'>
    <span className='help-block'>
      {cardError}
    </span>
  </div>
));

const CardDetails = branch(
  ({
    hasSubscription,
    subscriptionPaused,
    cardDetails
  }) => !hasSubscription || subscriptionPaused || !cardDetails,
  renderNothing
)(({
  cardDetails: {
    last4,
    brand
  }
}) => (
    <fieldset className='boxed'>
      <h2>Current card details</h2>
      <p className={classNames('card-details')}>
        {last4}
        <span className={fp.get(brand)({
          'Visa': 'visa',
          'MasterCard': 'mastercard',
          'American Express': 'amex'
        })} />
      </p>
    </fieldset>
  ));

const ReactivateBusinessSubscriptionModal = branch(
  ({ business }) => !business,
  renderNothing
)(({
  displaySubSelectionModal,
  dismissSubSelectionModal,
  smallTeamPlanName,
  confirmReactivateSubscription,
  individualPlanName,
  business
}) => (
    <Modal show={displaySubSelectionModal} onHide={dismissSubSelectionModal}>
      <Modal.Header closeButton>
        <Modal.Title componentClass='h2' className='text-center'>
          Reactivate your subscription
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-center'>
          It appears that you have an inactive {he.decode(smallTeamPlanName)} subscription for your business <b>{he.decode(business.name)}</b>.
          Would you like to reactivate your {he.decode(smallTeamPlanName)} plan?
        </p>
        <p className='text-center'>
          <Button onClick={() => confirmReactivateSubscription(false)}>
            Reactivate my business subscription
          </Button>
        </p>
        <p className='text-center'>
          Alternatively you can subscribe to {he.decode(individualPlanName)} plan.
          Note that you will no longer be able to manage <b>{he.decode(business.name)}</b> after you switch your account to {he.decode(individualPlanName)} plan.
        </p>
        <p className='text-center'>
          <Button onClick={() => confirmReactivateSubscription(true)}>
            Activate my subscription
          </Button>
        </p>
        <p className='text-center'>
          Need more information on plan options?
          <br />
          <a href={links.thinkCaddie.pricing} target='_blank'>
            Click here
          </a>.
        </p>
      </Modal.Body>
    </Modal>
  )
);

const Payment = ({
  componentRef,
  cardError,
  isSubmitting,
  submissionMessage,
  showSubmissionMessage,
  hideSubmissionMessage,
  allowMessageFocus,
  stopFocusingMessage,
  error,
  cardDetails,
  individualPlanId,
  smallTeamPlanId,
  planId,
  setCardError,
  subscription: {
    endDate,
    quantity,
    id: hasSubscription,
    isPaused: subscriptionPaused
  },
  onFormSubmit,
  isFetching,
  displaySubSelectionModal,
  dismissSubSelectionModal,
  obtainedStripeToken,
  business,
  confirmReactivateSubscription,
  cancelReactivateSubscription,
  individualPlanName,
  smallTeamPlanName,
  onCancelPlan,
  onUpdatePlan,
  plans,
  upcomingInvoice,
  businessError,
  checkServerError,
  hideServerError
}) => [
    <Row key={0} className='no-gutter'>
      {
        fp.includes(stripeErrorPrefix)(error) || fp.includes(stripeErrorPrefix)(businessError) ? null :
          <FormSubmissionMessage
            focusMessage={allowMessageFocus}
            showSubmissionMessage={showSubmissionMessage}
            submissionMessage={submissionMessage}
            error={error}
            stopFocusingMessage={stopFocusingMessage}
            hideSubmissionMessage={hideSubmissionMessage} />
      }
    </Row>,
    <Row key={1} className='no-gutter'>
      <Col xs={12} sm={5} smPush={7}>
        <PlanDetails {...{
          onCancelPlan,
          onUpdatePlan,
          upcomingInvoice,
        }} />
        {
          hasSubscription && planId === individualPlanId ?
            <PlanUpgrade {...{
              planId: smallTeamPlanId,
              individualPlanId,
              smallTeamPlanId,
              plans
            }} /> : null
        }
      </Col>
      <Col xs={12} sm={6} smPull={5}>
        <CardDetails {...{
          hasSubscription,
          subscriptionPaused,
          cardDetails
        }} />
        <form
          ref={(form) => {
            if (componentRef) {
              componentRef.formRef = form;
            }
          }}
          noValidate
          onSubmit={onFormSubmit}
          className='signup'>
          <fieldset className='payment'>
            <h2>Your payment details</h2>
            <CreditCardElements
              setCardError={setCardError}
              hideServerError={hideServerError}
              expirationPlaceholder='MM / YY' />
            {
              cardError ?
                <CardError {...{ cardError }} /> :
                null
            }
            {
              fp.includes(stripeErrorPrefix)(error) && checkServerError ?
                <div aria-invalid='true' className='has-error'>
                  <span className='help-block'>{fp.replace(stripeErrorPrefix)('')(error)}</span>
                </div> :
                null
            }
            {
              fp.includes(stripeErrorPrefix)(businessError) && checkServerError ?
                <div aria-invalid='true' className='has-error'>
                  <span className='help-block'>{fp.replace(stripeErrorPrefix)('')(businessError)}</span>
                </div> :
                null
            }
            <Button type='submit' className='large' disabled={isSubmitting}>
              {
                isSubmitting ?
                  <Spinner name='circle' className='spinner' fadeIn='none' /> :
                  hasSubscription ?
                    'Update payment details' :
                    'Activate My Subscription'
              }
            </Button>
          </fieldset>
        </form>
      </Col>
    </Row>,
    <ReactivateBusinessSubscriptionModal {...{
      key: 2,
      displaySubSelectionModal,
      dismissSubSelectionModal,
      smallTeamPlanName,
      confirmReactivateSubscription,
      individualPlanName,
      business
    }} />
  ];

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  userProfile: state.user.profile,
  isUpdating: state.user.isUpdating,
  error: state.user.error,
  config: stripeConfigSelector(state),
  businessSubscription: state.business.subscription,
  userSubscription: state.user.currentSubscription,
  upcomingInvoice: state.payment.upcomingInvoice,
  isFetching: state.user.isFetching || state.business.isFetching,
  plans: state.payment.plans,
  cardDetails: state.payment.cardDetails,
  business: businessSelector(state),
  businessError: state.business.error
});

const mapDispatchToProps = (dispatch) => ({
  getPlans: bindActionCreators(paymentActions.payment.getPlans, dispatch),
  getCardDetails: bindActionCreators(paymentActions.payment.getCardDetails, dispatch),
  getUpcomingInvoice: bindActionCreators(paymentActions.payment.getUpcomingInvoice, dispatch),
  getBusinessUpcomingInvoice: bindActionCreators(paymentActions.payment.getBusinessUpcomingInvoice, dispatch),
  updateUserPayment: bindActionCreators(userActions.user.updatePaymentDetails, dispatch),
  updateBusinessPayment: bindActionCreators(businessActions.business.updatePaymentDetails, dispatch)
});

const findPlan = (planId) => fp.find(({ id }) => id === planId);

export default compose(
  withStripe,
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({ 
    user,
    userProfile,
  }) => ({
    isUserBusinessAdmin: isBusinessAdmin(user),
    userIndustryName: userProfile !== undefined ? userProfile.stat.industryName : "",
  })),
  withProps(({
    businessSubscription,
    isUserBusinessAdmin,
    userSubscription
  }) => ({
    subscription: isUserBusinessAdmin ?
      notCanceled(businessSubscription) :
      notCanceled(userSubscription),
  })),
  withProps(({
    subscription,
    config: {
      plansInUse,
    },
    userIndustryName,
  }) => ({
    planId: subscription.planId,
    individualPlanId: getIndividualPlanIdByUserIndustry(plansInUse, userIndustryName),
    smallTeamPlanId: getSmallTeamPlanIdByUserIndustry(plansInUse, userIndustryName)
  })),
  withProps(({
    individualPlanId,
    smallTeamPlanId,
    planId,
    plans
  }) => ({
    individualPlanName: fp.flow(
      findPlan(individualPlanId),
      fp.getOr('Individual')('name')
    )(plans),
    plan: findPlan(planId)(plans),
    smallTeamPlanName: fp.flow(
      findPlan(smallTeamPlanId),
      fp.getOr('Organisations &amp; dealer groups')('name')
    )(plans)
  })),
  withStateHandlers({
    cardError: null,
    submitted: false,
    isSubmitting: false,
    submissionMessage: '',
    showSubmissionMessage: false,
    allowMessageFocus: true,
    displaySubSelectionModal: false,
    obtainedStripeToken: null,
    checkServerError: false
  }, {
      finishedUpdating: () => () => ({
        allowMessageFocus: true,
        isSubmitting: false,
        showSubmissionMessage: true,
        submissionMessage: 'Your payment details were successfully updated'
      }),
      onCancelPlan: () => () => ({
        showSubmissionMessage: true,
        submissionMessage: 'Your plan was successfully cancelled'
      }),
      onUpdatePlan: () => () => ({
        showSubmissionMessage: true,
        submissionMessage: 'Your plan was successfully updated'
      }),
      hideSubmissionMessage: () => () => ({
        showSubmissionMessage: false
      }),
      setCardError: () => (error) => ({
        cardError: error ?
          error.message :
          null
      }),
      showServerError: () => () => ({
        checkServerError: true
      }),
      hideServerError: () => () => ({
        checkServerError: false
      }),
      setSubmitting: () => (isSubmitting) => ({
        isSubmitting
      }),
      submittedForm: () => () => ({
        submitted: true,
        showSubmissionMessage: false
      }),
      stopFocusingMessage: () => () => ({
        allowMessageFocus: false
      }),
      showSubSelectionModal: () => () => ({
        displaySubSelectionModal: true
      }),
      dismissSubSelectionModal: () => () => ({
        displaySubSelectionModal: false,
        isSubmitting: false
      }),
      setObtainedStripeToken: () => (obtainedStripeToken) => ({
        obtainedStripeToken
      })
    }),
  lifecycle({
    componentDidMount() {
      const {
        user,
        business,
        getPlans,
        getCardDetails,
        getUpcomingInvoice,
        getBusinessUpcomingInvoice,
      } = this.props;

      getPlans();
      getCardDetails();
      if (isBusinessAdmin(user)) {
        getBusinessUpcomingInvoice(business.id);
      } else {
        getUpcomingInvoice();
      }
      
      this.setState({ componentRef: this });
    },
    componentWillReceiveProps({ isUpdating }) {
      const {
        isUpdating: wasUpdating,
        getCardDetails,
        finishedUpdating,
        showServerError
      } = this.props;

      if (wasUpdating && !isUpdating) {
        getCardDetails();
        finishedUpdating();
        showServerError();
      }
    }
  }),
  withHandlers({
    onFormSubmit: ({
      cardError,
      isSubmitting,
      setCardError,
      setSubmitting,
      stripe,
      elements,
      submittedForm,
      updatePayment,
      componentRef,
      setObtainedStripeToken,
      showSubSelectionModal,
      updateUserPayment,
      updateBusinessPayment,
      subscription,
      isUserBusinessAdmin,
      business,
      hideServerError
    }) => async (event) => {
      event.preventDefault();
      event.stopPropagation();

      const hasSubscription = subscription.id;
      submittedForm();
      hideServerError();

      if (!isSubmitting && !cardError) {
        setSubmitting(true);

        const cardElements = elements.getElement('cardNumber');

        stripe.createToken(cardElements).then(({ token, error }) => {
          setCardError(error);

          if (token && !error) {
            if (isUserBusinessAdmin) {
              setObtainedStripeToken(token);
              hasSubscription ?
                updateBusinessPayment(business.id, token.id) :
                showSubSelectionModal();
            }
            else {
              updateUserPayment(token.id, false);
            }
          }
          else {
            setSubmitting(false);
          }
        });
      }
      else if (componentRef.formRef && cardError) {
        handleInvalidForm(componentRef.formRef);
      }
    },
    confirmReactivateSubscription: ({
      user,
      business,
      isUserBusinessAdmin,
      updateBusinessPayment,
      updateUserPayment,
      obtainedStripeToken,
      dismissSubSelectionModal
    }) => (detachBusinessAdmin) => {
      if (isUserBusinessAdmin && !detachBusinessAdmin) {
        updateBusinessPayment(business.id, obtainedStripeToken.id);
      }
      else {
        updateUserPayment(obtainedStripeToken.id, detachBusinessAdmin);
      }

      dismissSubSelectionModal();
    },
    cancelReactivateSubscription: ({
      dismissSubSelectionModal,
      setSubmitting
    }) => () => {
      dismissSubSelectionModal();
      setSubmitting(false);
    }
  }),
  branch(
    ({ plans }) => !(plans && plans.length),
    renderComponent(Loading)
  ),
  branch(
    ({ user }) => isBusinessUser(user) && !isBusinessAdminOnly(user),
    renderComponent(TeamMemberPayment)
  )
)(Payment);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/partials/payment.js